/**
 * Centralized TanStack Query key factory.
 *
 * These keys are used by hooks to watch PowerSync tables
 * and auto-invalidate via TanStack Query.
 *
 * Rules:
 *   - Every PowerSync-backed or server-backed query MUST use a key from here.
 *   - Never use inline string arrays as queryKey — use this factory instead.
 *   - Keys are grouped by domain and ordered from broad → specific.
 */
export const queryKeys = {
  accounts: {
    all: ["accounts"] as const,
    /** User accounts (wallets) with group metadata. */
    list: (userId: string | undefined) => ["accounts", "list", userId] as const,
    /** Derived live balances (initial_balance + tx aggregation). */
    balances: (userId: string | undefined) => ["accounts", "balances", userId] as const,
    /** Account groups. */
    groups: (userId: string | undefined) => ["accounts", "groups", userId] as const,
    /** Detail of an account. */
    detail: (walletId: string | undefined) => ["accounts", "detail", walletId] as const,
    /** Reconciliation of an account (total vs verified balance). */
    reconciliation: (userId: string | undefined, walletId: string | undefined) =>
      ["accounts", "reconciliation", userId, walletId] as const,
  },
  categories: {
    all: ["categories"] as const,
    /** All categories (tree is built in memory). */
    list: (userId: string | undefined) => ["categories", "list", userId] as const,
  },
  /** ACTIVITY-CORE: activity feed + history per entity (transaction). */
  activity: {
    all: ["activity"] as const,
    /** Global user feed (activity_recent bucket). */
    feed: (userId: string | undefined) => ["activity", "feed", userId] as const,
    /** History of a specific entity ("history per transaction"). */
    entity: (userId: string | undefined, entityType: string, entityId: string) =>
      ["activity", "entity", userId, entityType, entityId] as const,
  },
  /** Trash (L2): entities with deleted_at IS NOT NULL. */
  trash: {
    all: ["trash"] as const,
    transactions: (userId: string | undefined) => ["trash", "transactions", userId] as const,
    wallets: (userId: string | undefined) => ["trash", "wallets", userId] as const,
    categories: (userId: string | undefined) => ["trash", "categories", userId] as const,
  },
  transactions: {
    all: ["transactions"] as const,
    /** Transactions in an accounting period with filters. */
    list: <T extends object>(options: T) => ["transactions", "list", options] as const,
    /** Paginated variant (incremental limit). */
    listPaged: <T extends object>(options: T, limit: number) =>
      ["transactions", "list", options, limit] as const,
    /** Detail of a transaction. */
    detail: (txId: string | undefined) => ["transactions", "detail", txId] as const,
    /** Period summary (expense, income, balance). */
    summary: (userId: string | undefined, from: string, to: string) =>
      ["transactions", "summary", userId, from, to] as const,
  },
  budgets: {
    all: ["budgets"] as const,
    /** User budgets (global and per category). */
    list: (userId: string | undefined) => ["budgets", "list", userId] as const,
    /** Progress (spent vs budgeted) of an accounting period. */
    progress: (userId: string | undefined, from: string, to: string) =>
      ["budgets", "progress", userId, from, to] as const,
  },
  recurring: {
    all: ["recurring"] as const,
    /** User recurring rules. */
    list: (userId: string | undefined) => ["recurring", "list", userId] as const,
  },
  exchangeRates: {
    /** Global rate catalog (vs USD pivot). */
    all: ["exchangeRates"] as const,
    /** User rates by date (editable, offline). */
    user: (userId: string | undefined) => ["exchangeRates", "user", userId] as const,
  },
  projects: {
    all: ["projects"] as const,
    /** User projects. */
    list: (userId: string | undefined) => ["projects", "list", userId] as const,
    /** Detail of a project. */
    detail: (projectId: string | undefined) => ["projects", "detail", projectId] as const,
    /** Aggregated totals (expense/income) per project. */
    totals: (userId: string | undefined) => ["projects", "totals", userId] as const,
  },
  debts: {
    all: ["debts"] as const,
    /** User debts. */
    list: (userId: string | undefined) => ["debts", "list", userId] as const,
    /** Payments of a debt. */
    payments: (debtId: string | undefined) => ["debts", "payments", debtId] as const,
  },
  reports: {
    all: ["reports"] as const,
    /** Category breakdown in a range. */
    byCategory: (userId: string | undefined, from: string, to: string) =>
      ["reports", "byCategory", userId, from, to] as const,
    /** Temporal trend (series per day/month) in a range. */
    trend: (userId: string | undefined, from: string, to: string) =>
      ["reports", "trend", userId, from, to] as const,
    /** Account breakdown in a range. */
    byAccount: (userId: string | undefined, from: string, to: string) =>
      ["reports", "byAccount", userId, from, to] as const,
    /** Payee breakdown in a range. */
    byPayee: (userId: string | undefined, from: string, to: string) =>
      ["reports", "byPayee", userId, from, to] as const,
    /** Project breakdown in a range. */
    byProject: (userId: string | undefined, from: string, to: string) =>
      ["reports", "byProject", userId, from, to] as const,
    /** Top expenses in a range. */
    topExpenses: (userId: string | undefined, from: string, to: string) =>
      ["reports", "topExpenses", userId, from, to] as const,
    /** Comparison between two accounting periods. */
    periodCompare: (userId: string | undefined, from: string, to: string) =>
      ["reports", "periodCompare", userId, from, to] as const,
  },
  templates: {
    all: ["templates"] as const,
    /** User transaction templates. */
    list: (userId: string | undefined) => ["templates", "list", userId] as const,
  },
  payeeRules: {
    all: ["payeeRules"] as const,
    /** Auto-categorization rules by payee. */
    list: (userId: string | undefined) => ["payeeRules", "list", userId] as const,
  },
  payees: {
    all: ["payees"] as const,
    /** Payee suggestions (autocomplete) by prefix. */
    suggestions: (userId: string | undefined, query: string) =>
      ["payees", "suggestions", userId, query] as const,
    /** Categories used in history for an exact payee (auto-categorization). */
    categoryHistory: (userId: string | undefined, payee: string) =>
      ["payees", "categoryHistory", userId, payee] as const,
  },
  dataIO: {
    all: ["dataIO"] as const,
    /** Import preview (CSV/AndroMoney) before confirming. */
    importPreview: (hash: string) => ["dataIO", "importPreview", hash] as const,
    /** Last export/backup status. */
    exportStatus: (userId: string | undefined) => ["dataIO", "exportStatus", userId] as const,
  },
  search: {
    all: ["search"] as const,
    results: (query: string, filter: string) => ["search", "results", query, filter] as const,
  },
  powersync: {
    /** Upload queue stats (sync indicator). */
    uploadStats: (userId: string | undefined) => ["powersync", "uploadStats", userId] as const,
    /** Is there local data? (cleanup button in profile). */
    hasLocalData: (userId: string | undefined) => ["powersync", "hasLocalData", userId] as const,
  },
  themes: {
    all: ["themes"] as const,
    /** Full theme catalog (getThemesQuery without pagination; profile + list-dialog SHARE the cache). */
    catalog: () => ["themes", "catalog"] as const,
    /** Gallery of all available themes (paginated).*/
    gallery: (limit?: number) => ["themes", "gallery", limit] as const,
    /** Active theme row resolved for a specific user.*/
    active: (userId: string | undefined) => ["themes", "active", userId] as const,
    /** Raw user_settings row (stores selected theme id).*/
    userSettings: (userId: string | undefined) => ["themes", "userSettings", userId] as const,
  },
  userPreferences: {
    /** Consolidated user_preferences row (language, font, theme).*/
    consolidated: (userId: string | undefined) =>
      ["userPreferences", "consolidated", userId] as const,
    /** User financial settings (base currency, month start day). */
    financeSettings: (userId: string | undefined) =>
      ["userPreferences", "financeSettings", userId] as const,
  },
  attachments: {
    all: ["attachments"] as const,
    /** Attachments (receipts) of a transaction. */
    byTx: (txId: string) => ["attachments", txId] as const,
  },
  fonts: {
    all: ["fonts"] as const,
    families: (category?: string) => ["fonts", "families", category] as const,
    family: (id: string | null) => ["fonts", "family", id] as const,
    familyVariants: (id: string | null) => ["fonts", "variants", id] as const,
    variants: () => ["fonts", "variants"] as const,
    /** Active font family for a user (from user_preferences).*/
    userFamily: (userId: string | undefined) => ["fonts", "userFamily", userId] as const,
    /** Full font families catalog (profile font selector).*/
    catalogFamilies: () => ["fonts", "catalogFamilies"] as const,
    /** Full font variants catalog (profile font selector).*/
    catalogVariants: () => ["fonts", "catalogVariants"] as const,
    /** Presence probe: is the synced catalog available locally at all?*/
    catalogPresence: () => ["fonts", "catalogPresence"] as const,
  },
  /** Admin font management (server REST, not PowerSync).*/
  adminFonts: {
    /** The approved catalog (listAll). Prefix of `registry` so invalidating it
     *  cascades to both lists.*/
    all: ["admin", "fonts"] as const,
    /** Third-party registry browser, keyed by the active filters.*/
    registry: (search: string, category: string) =>
      ["admin", "fonts", "registry", search, category] as const,
  },
  auth: {
    invitations: () => ["auth", "listInvitations"] as const,
    organizations: () => ["auth", "listOrganizations"] as const,
    members: (organizationId: string) =>
      ["auth", "getOrganizationMembers", { organizationId }] as const,
    session: () => ["auth", "session"] as const,
    socialProviders: () => ["auth", "socialProviders"] as const,
    loginMethods: () => ["auth", "loginMethods"] as const,
    /**
     * Active devices session list prefix (profile).
     *
     * Exposed separately from `activeSessionsList` because invalidation after a
     * successful revocation does not know (or care about) current session id
     * carried as second segment: invalidates by prefix.
     */
    activeSessions: () => ["active-sessions"] as const,
    /** Active sessions list as requested by profile. */
    activeSessionsList: (currentSessionId: string | undefined) =>
      ["active-sessions", currentSessionId] as const,
  },
};
